import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  getHotelData, 
  exportHotelsToCSV,
  extractWhatsAppNumbers,
  Hotel
} from '@/data/mocks/hotelPartnerships';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  MessageSquare,
  Download,
  Target,
  TrendingUp,
  Users,
  Euro,
  CheckCircle2,
  Clock,
  Zap,
  Copy,
  Star,
  AlertTriangle,
  Building,
  ExternalLink,
  FileText,
  Loader2
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const HotelPartnershipsPage: React.FC = () => {
  const [copiedText, setCopiedText] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { hotels, strategy, stats } = getHotelData('cafe-max');

  if (!hotels || !strategy) {
    return <div className="p-8">Chargement...</div>;
  }

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(type);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const downloadCSV = () => {
    const csv = exportHotelsToCSV('cafe-max');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `hotels-cafe-max-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const downloadWhatsApp = () => {
    const numbers = extractWhatsAppNumbers('cafe-max');
    const text = numbers.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'whatsapp-hotels-cafe-max.txt';
    link.click();
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Helper function to add new page
      const addNewPage = () => {
        pdf.addPage();
        yPosition = margin;
      };

      // Helper function to check if we need a new page
      const checkPageBreak = (heightNeeded: number) => {
        if (yPosition + heightNeeded > pageHeight - margin) {
          addNewPage();
          return true;
        }
        return false;
      };

      // ========== PAGE 1: COUVERTURE ==========
      // Background gradient effect
      pdf.setFillColor(249, 250, 251);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Header with gold accent
      pdf.setFillColor(218, 165, 32);
      pdf.rect(0, 0, pageWidth, 50, 'F');

      // Logo/Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(32);
      pdf.setFont('helvetica', 'bold');
      pdf.text('STRATOS', pageWidth / 2, 25, { align: 'center' });
      
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Digital Strategy', pageWidth / 2, 35, { align: 'center' });

      // Title
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      yPosition = 80;
      pdf.text('Stratégie Partenariats', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 12;
      pdf.text('Hôteliers', pageWidth / 2, yPosition, { align: 'center' });

      // Subtitle
      yPosition += 20;
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      pdf.text('Café Max - Lyon', pageWidth / 2, yPosition, { align: 'center' });

      // Key Numbers Box
      yPosition += 30;
      pdf.setFillColor(34, 197, 94);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 60, 3, 3, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      yPosition += 12;
      pdf.text('POTENTIEL IDENTIFIÉ', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 15;
      pdf.setFontSize(36);
      pdf.text('814k€/an', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 10;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('15 hôtels 4★ + Opportunités privatisation', pageWidth / 2, yPosition, { align: 'center' });

      // Stats cards
      yPosition += 25;
      const cardWidth = (pageWidth - 4 * margin) / 3;
      
      // Card 1
      pdf.setFillColor(59, 130, 246);
      pdf.roundedRect(margin, yPosition, cardWidth, 25, 2, 2, 'F');
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('15', margin + cardWidth / 2, yPosition + 10, { align: 'center' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Hôtels 4★', margin + cardWidth / 2, yPosition + 18, { align: 'center' });

      // Card 2
      pdf.setFillColor(168, 85, 247);
      pdf.roundedRect(margin + cardWidth + 5, yPosition, cardWidth, 25, 2, 2, 'F');
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('720', margin + cardWidth + 5 + cardWidth / 2, yPosition + 10, { align: 'center' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Chambres', margin + cardWidth + 5 + cardWidth / 2, yPosition + 18, { align: 'center' });

      // Card 3
      pdf.setFillColor(218, 165, 32);
      pdf.roundedRect(margin + 2 * (cardWidth + 5), yPosition, cardWidth, 25, 2, 2, 'F');
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('+33', margin + 2 * (cardWidth + 5) + cardWidth / 2, yPosition + 10, { align: 'center' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Résa/mois', margin + 2 * (cardWidth + 5) + cardWidth / 2, yPosition + 18, { align: 'center' });

      // Date and footer
      pdf.setFontSize(10);
      pdf.setTextColor(156, 163, 175);
      pdf.setFont('helvetica', 'normal');
      const dateStr = new Date().toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      pdf.text(dateStr, pageWidth / 2, pageHeight - 20, { align: 'center' });
      pdf.text('Document confidentiel - Stratos Digital Strategy', pageWidth / 2, pageHeight - 10, { align: 'center' });

      // ========== PAGE 2: SYNTHÈSE FINANCIÈRE ==========
      addNewPage();
      
      // Section title
      pdf.setFillColor(218, 165, 32);
      pdf.rect(margin, yPosition, pageWidth - 2 * margin, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SYNTHÈSE FINANCIÈRE', margin + 5, yPosition + 8);
      
      yPosition += 20;

      // Partenariats Hôteliers
      pdf.setFillColor(240, 253, 244);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 45, 2, 2, 'F');
      
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('💼 Partenariats Hôteliers', margin + 5, yPosition + 8);
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      yPosition += 15;
      pdf.text('• 15 hôtels 4★ dans un rayon de 2km', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• 720 chambres totales, taux occupation 76%', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• 8% des guests demandent une recommandation restaurant', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• 33 réservations/mois attendues (panier moyen 35€)', margin + 10, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(34, 197, 94);
      pdf.text('CA annuel : 118 000€', pageWidth - margin - 5, yPosition, { align: 'right' });

      // Privatisation B2C
      yPosition += 15;
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 50, 2, 2, 'F');
      
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('🎉 Privatisation B2C', margin + 5, yPosition + 8);
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      yPosition += 15;
      pdf.text('• Événements familiaux : 4-6/mois (45-65€/pers)', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• Groupes touristiques : 3-5/mois (55-75€/pers)', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• Mariages & fiançailles : 2-4/mois (60-90€/pers)', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• Total : 9-15 événements/mois', margin + 10, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(59, 130, 246);
      pdf.text('CA annuel : 252 000€', pageWidth - margin - 5, yPosition, { align: 'right' });

      // Privatisation B2B
      yPosition += 15;
      pdf.setFillColor(255, 247, 237);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 55, 2, 2, 'F');
      
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('💼 Privatisation B2B', margin + 5, yPosition + 8);
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      yPosition += 15;
      pdf.text('• Déjeuners affaires : 15-25/mois (55-85€/pers)', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• Séminaires & formations : 4-8/mois (packages complets)', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• Team building & événementiel : 5-10/mois (50-75€/pers)', margin + 10, yPosition);
      yPosition += 6;
      pdf.text('• Total : 24-43 événements/mois', margin + 10, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(249, 115, 22);
      pdf.text('CA annuel : 444 000€', pageWidth - margin - 5, yPosition, { align: 'right' });

      // Total
      yPosition += 15;
      checkPageBreak(30);
      pdf.setFillColor(34, 197, 94);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 25, 2, 2, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('POTENTIEL TOTAL ANNUEL', margin + 5, yPosition + 10);
      
      pdf.setFontSize(24);
      pdf.text('814 000€', pageWidth - margin - 5, yPosition + 10, { align: 'right' });
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('ROI : +32 460% (investissement 2 500€)', margin + 5, yPosition + 19);

      // ========== PAGE 3: TOP 5 HÔTELS PRIORITAIRES ==========
      addNewPage();
      
      pdf.setFillColor(34, 197, 94);
      pdf.rect(margin, yPosition, pageWidth - 2 * margin, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('TOP 5 HÔTELS PRIORITAIRES', margin + 5, yPosition + 8);
      
      yPosition += 18;

      const top5Hotels = stats?.priorityHotels.slice(0, 5) || [];
      top5Hotels.forEach((hotel, index) => {
        checkPageBreak(40);
        
        // Hotel card
        const isHighPriority = index < 3;
        pdf.setFillColor(isHighPriority ? 240 : 249, isHighPriority ? 253 : 250, isHighPriority ? 244 : 251);
        pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 35, 2, 2, 'F');
        
        // Number badge
        pdf.setFillColor(34, 197, 94);
        pdf.circle(margin + 8, yPosition + 8, 5, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text((index + 1).toString(), margin + 8, yPosition + 11, { align: 'center' });
        
        // Hotel name
        pdf.setTextColor(31, 41, 55);
        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.text(hotel.name, margin + 18, yPosition + 10);
        
        // Details
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(75, 85, 99);
        yPosition += 18;
        pdf.text(`📍 ${hotel.distance}m  •  🛏️ ${hotel.rooms} chambres  •  📊 ${hotel.occupancyRate}% occupation`, margin + 18, yPosition);
        
        yPosition += 6;
        pdf.setFontSize(9);
        pdf.setTextColor(107, 114, 128);
        pdf.text(`📱 ${hotel.contact.whatsapp || hotel.contact.phone}`, margin + 18, yPosition);
        pdf.text(`✉️ ${hotel.contact.email}`, margin + 95, yPosition);
        
        if (hotel.contact.concierge) {
          yPosition += 6;
          pdf.setTextColor(37, 99, 235);
          pdf.text(`👤 Concierge : ${hotel.contact.concierge}`, margin + 18, yPosition);
        }
        
        yPosition += 12;
      });

      // ========== PAGE 4: ACTIONS RECOMMANDÉES ==========
      addNewPage();
      
      pdf.setFillColor(218, 165, 32);
      pdf.rect(margin, yPosition, pageWidth - 2 * margin, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PLAN D\'ACTION RECOMMANDÉ', margin + 5, yPosition + 8);
      
      yPosition += 20;

      // Actions B2C
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 50, 2, 2, 'F');
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('🎯 Actions Immédiates - B2C', margin + 5, yPosition + 8);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      yPosition += 15;
      pdf.text('✓ Créer page web "Privatisation" avec formulaire + tarifs packages', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('✓ Partenariat wedding planners : Commission 10-15% sur apports', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('✓ Instagram Stories : Montrer ambiance privatisations passées', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('✓ Offre early bird : -15% si réservation 3 mois avant événement', margin + 8, yPosition);

      // Actions B2B
      yPosition += 15;
      pdf.setFillColor(255, 247, 237);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 50, 2, 2, 'F');
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('💼 Actions Immédiates - B2B', margin + 5, yPosition + 8);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      yPosition += 15;
      pdf.text('✓ LinkedIn B2B prospecting : Cibler DRH, Office Managers Lyon', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('✓ Packages séminaires clés en main : Petit-déj + déj + dîner', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('✓ Partenariat espaces coworking : WeWork, Spaces, Morning (Lyon)', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('✓ Contrats récurrents entreprises : Forfait mensuel déjeuners équipe', margin + 8, yPosition);

      // Timeline
      yPosition += 20;
      checkPageBreak(70);
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 60, 2, 2, 'F');
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('📅 Timeline Déploiement', margin + 5, yPosition + 8);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      yPosition += 15;
      pdf.text('Semaine 1-2 : Contacter top 5 hôtels prioritaires (WhatsApp + Email)', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('Semaine 2-3 : Relance et rendez-vous physiques', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('Semaine 3-4 : Finalisation accords et mise en place matériel', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('Mois 2 : Lancement page privatisation + campagne LinkedIn B2B', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('Mois 3 : Extension à 10 autres hôtels + partenariats coworking', margin + 8, yPosition);
      yPosition += 6;
      pdf.text('Objectif : 67% des hôtels convertis en partenaires actifs', margin + 8, yPosition);

      // Footer on last page
      yPosition = pageHeight - 30;
      pdf.setFillColor(249, 250, 251);
      pdf.rect(0, yPosition, pageWidth, 30, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      yPosition += 10;
      pdf.text('Document généré par STRATOS - Digital Strategy Platform', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 6;
      pdf.text('Pour plus d\'informations : contact@stratos-digital.com', pageWidth / 2, yPosition, { align: 'center' });

      // Save PDF
      const fileName = `Stratos_Partenariats_Hotels_CafeMax_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const filteredHotels = selectedFilter === 'all' 
    ? hotels 
    : hotels.filter(h => h.partnershipPotential === selectedFilter);

  return (
    <div className="space-y-8">
      {/* En-tête */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-stratos-gold to-gray-900 bg-clip-text text-transparent flex items-center gap-3">
              <Building2 className="w-10 h-10 text-stratos-gold" />
              Partenariats Hôteliers
            </h1>
            <p className="text-gray-600 mt-2 text-lg">
              Stratégie de référencement par les conciergeries - Café Max
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="outline">{hotels.length} hôtels 4★ identifiés</Badge>
              <Badge variant="outline">{stats?.totalRooms} chambres total</Badge>
              <Badge className="bg-green-600 text-white">+118k€/an de potentiel</Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={downloadCSV} variant="outline" disabled={isGeneratingPDF}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={downloadWhatsApp} className="bg-green-600 hover:bg-green-700 text-white" disabled={isGeneratingPDF}>
              <MessageSquare className="w-4 h-4 mr-2" />
              Numéros WhatsApp
            </Button>
            <Button 
              onClick={generatePDF} 
              className="bg-stratos-gold hover:bg-yellow-600 text-white font-semibold"
              disabled={isGeneratingPDF}
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF Pro
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* KPIs Principaux */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">Hôtels 4★</p>
                  <p className="text-5xl font-bold text-blue-600">{strategy.totalHotels}</p>
                  <p className="text-xs text-gray-500">Dans rayon 2km</p>
                </div>
                <div className="p-4 bg-blue-100 rounded-full">
                  <Building className="w-10 h-10 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">Chambres Total</p>
                  <p className="text-5xl font-bold text-purple-600">{strategy.totalRooms}</p>
                  <p className="text-xs text-gray-500">Capacité totale</p>
                </div>
                <div className="p-4 bg-purple-100 rounded-full">
                  <Users className="w-10 h-10 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="border-2 border-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">Réservations/mois</p>
                  <p className="text-5xl font-bold text-green-600">+{strategy.estimatedPotential.expectedReservations}</p>
                  <p className="text-xs text-green-600 font-semibold">Potentiel estimé</p>
                </div>
                <div className="p-4 bg-green-100 rounded-full">
                  <TrendingUp className="w-10 h-10 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card className="border-2 border-stratos-gold">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">CA Additionnel/an</p>
                  <p className="text-5xl font-bold text-stratos-gold">
                    {Math.round(strategy.estimatedPotential.annualRevenue / 1000)}k€
                  </p>
                  <p className="text-xs text-stratos-gold font-semibold">ROI: +4 650%</p>
                </div>
                <div className="p-4 bg-yellow-100 rounded-full">
                  <Euro className="w-10 h-10 text-stratos-gold" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Calcul Détaillé */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-6 h-6 text-blue-600" />
              Calcul du Potentiel - Méthode Conservative
            </CardTitle>
            <CardDescription>Basé sur les standards de l'industrie hôtelière parisienne</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 text-lg mb-3">📊 Données d'Entrée</h4>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-blue-200">
                    <span className="text-gray-700">Total chambres</span>
                    <span className="font-bold">{strategy.totalRooms}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-blue-200">
                    <span className="text-gray-700">Taux occupation moyen</span>
                    <span className="font-bold">76%</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-blue-200">
                    <span className="text-gray-700">Guests/mois</span>
                    <span className="font-bold">{strategy.estimatedPotential.guestsPerMonth.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-blue-200">
                    <span className="text-gray-700">Demandent restaurant</span>
                    <span className="font-bold">8% (standard)</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-700">Conversion finale</span>
                    <span className="font-bold">15% (avec partenariat)</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-900 text-lg mb-3">💰 Résultats Financiers</h4>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-green-200">
                    <span className="text-gray-700">Réservations/mois</span>
                    <span className="font-bold text-green-600">{strategy.estimatedPotential.expectedReservations}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-green-200">
                    <span className="text-gray-700">Panier moyen</span>
                    <span className="font-bold">{strategy.estimatedPotential.averageBill}€</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-green-200">
                    <span className="text-gray-700">CA/mois</span>
                    <span className="font-bold text-green-600">{strategy.estimatedPotential.monthlyRevenue.toLocaleString()}€</span>
                  </div>
                  <div className="flex justify-between py-3 border-t-2 border-green-600 mt-2">
                    <span className="text-gray-900 font-bold text-lg">CA/an</span>
                    <span className="font-bold text-2xl text-green-600">{strategy.estimatedPotential.annualRevenue.toLocaleString()}€</span>
                  </div>
                  <div className="flex justify-between text-sm bg-white p-2 rounded">
                    <span className="text-gray-600">ROI (vs invest 2 500€)</span>
                    <span className="font-bold text-green-600">+4 650%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Statistiques par Proximité */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-2 border-green-500">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-600" />
              0-500m (Priorité 1)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-green-600 mb-2">
              {stats?.byDistance['0-500m']} hôtels
            </div>
            <p className="text-sm text-gray-600">À portée de marche immédiate</p>
            <p className="text-xs text-green-600 font-semibold mt-2">5 min à pied maximum</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-yellow-500">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-yellow-600" />
              500m-1km (Priorité 2)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-yellow-600 mb-2">
              {stats?.byDistance['500m-1km']} hôtels
            </div>
            <p className="text-sm text-gray-600">Distance raisonnable</p>
            <p className="text-xs text-yellow-600 font-semibold mt-2">5-10 minutes à pied</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-500">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              1-2km (Opportunité)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-blue-600 mb-2">
              {stats?.byDistance['1-2km']} hôtels
            </div>
            <p className="text-sm text-gray-600">Taxi ou métro</p>
            <p className="text-xs text-blue-600 font-semibold mt-2">10-15 minutes</p>
          </CardContent>
        </Card>
      </div>

      {/* Liste Complète des Hôtels */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Liste des 15 Hôtels 4★ à Contacter</CardTitle>
                <CardDescription className="text-base mt-2">
                  {stats?.byPotential.high} hôtels à fort potentiel • {stats?.byPotential.medium} potentiel moyen • {stats?.byPotential.low} potentiel faible
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={selectedFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setSelectedFilter('all')}
                >
                  Tous ({hotels.length})
                </Button>
                <Button
                  size="sm"
                  variant={selectedFilter === 'high' ? 'default' : 'outline'}
                  onClick={() => setSelectedFilter('high')}
                  className={selectedFilter === 'high' ? 'bg-green-600' : ''}
                >
                  🔥 High ({stats?.byPotential.high})
                </Button>
                <Button
                  size="sm"
                  variant={selectedFilter === 'medium' ? 'default' : 'outline'}
                  onClick={() => setSelectedFilter('medium')}
                >
                  Medium ({stats?.byPotential.medium})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Hôtel</TableHead>
                  <TableHead>Distance</TableHead>
                  <TableHead>Capacité</TableHead>
                  <TableHead>Contacts</TableHead>
                  <TableHead>Potentiel</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHotels
                  .sort((a, b) => a.distance - b.distance)
                  .map((hotel, index) => (
                    <TableRow 
                      key={hotel.id}
                      className={hotel.partnershipPotential === 'high' ? 'bg-green-50 border-l-4 border-green-500' : ''}
                    >
                      <TableCell className="font-bold text-gray-400">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">{hotel.name}</span>
                            <Badge variant="outline" className="text-xs">{hotel.category}</Badge>
                          </div>
                          <p className="text-xs text-gray-500">{hotel.address}</p>
                          {hotel.contact.concierge && (
                            <p className="text-xs text-blue-600">
                              👤 Concierge : {hotel.contact.concierge}
                            </p>
                          )}
                          {hotel.notes && (
                            <p className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded inline-block">
                              💡 {hotel.notes}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="font-bold">{hotel.distance}m</span>
                            <p className="text-xs text-gray-500">
                              {hotel.distance < 500 ? '🟢 5 min' : hotel.distance < 1000 ? '🟡 10 min' : '🔵 15 min'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-bold">{hotel.rooms} chambres</p>
                          <p className="text-xs text-gray-500">
                            ~{Math.round(hotel.rooms * hotel.occupancyRate / 100)}/jour occupées
                          </p>
                          <p className="text-xs text-blue-600">
                            {hotel.clientProfile === 'luxury' ? '💎 Luxe' :
                             hotel.clientProfile === 'business' ? '💼 Affaires' :
                             hotel.clientProfile === 'tourist' ? '🧳 Tourisme' : '🔀 Mixte'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full justify-start text-xs"
                            onClick={() => copyToClipboard(hotel.contact.whatsapp || hotel.contact.phone, `wa-${hotel.id}`)}
                          >
                            <MessageSquare className="w-3 h-3 mr-2" />
                            {hotel.contact.whatsapp || 'N/A'}
                            {copiedText === `wa-${hotel.id}` && (
                              <CheckCircle2 className="w-3 h-3 ml-auto text-green-600" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full justify-start text-xs"
                            onClick={() => copyToClipboard(hotel.contact.email, `email-${hotel.id}`)}
                          >
                            <Mail className="w-3 h-3 mr-2" />
                            {hotel.contact.email.length > 18 ? hotel.contact.email.substring(0,18) + '...' : hotel.contact.email}
                            {copiedText === `email-${hotel.id}` && (
                              <CheckCircle2 className="w-3 h-3 ml-auto text-green-600" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={hotel.partnershipPotential === 'high' ? 'default' : 'outline'}
                          className={hotel.partnershipPotential === 'high' ? 'bg-green-600' : ''}
                        >
                          {hotel.partnershipPotential === 'high' ? '🔥 High' : 
                           hotel.partnershipPotential === 'medium' ? '⭐ Medium' : 
                           '📌 Low'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => window.open(`https://wa.me/${hotel.contact.whatsapp?.replace(/\D/g, '')}`, '_blank')}
                          className="bg-green-600 hover:bg-green-700 text-white w-full"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          WhatsApp
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>

      {/* Top 5 Prioritaires */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Card className="border-2 border-green-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Star className="w-8 h-8 text-green-600" />
              Top 5 Hôtels Prioritaires - À Contacter CETTE SEMAINE
            </CardTitle>
            <CardDescription className="text-base">
              Les 5 meilleurs candidats pour démarrer rapidement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.priorityHotels.slice(0, 5).map((hotel, index) => (
                <div
                  key={hotel.id}
                  className="flex items-center justify-between p-5 bg-green-50 rounded-lg border-2 border-green-200 hover:border-green-400 transition-colors"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-600 text-white font-bold text-lg flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-lg mb-1">{hotel.name}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {hotel.distance}m
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {hotel.rooms} chambres
                        </span>
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          {hotel.occupancyRate}% occupation
                        </span>
                      </div>
                      {hotel.contact.concierge && (
                        <p className="text-sm text-blue-700 font-medium mb-1">
                          👤 Concierge : {hotel.contact.concierge}
                        </p>
                      )}
                      {hotel.notes && (
                        <p className="text-sm text-green-700 font-semibold">
                          💡 {hotel.notes}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500">📱 {hotel.contact.whatsapp}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">✉️ {hotel.contact.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      onClick={() => window.open(`https://wa.me/${hotel.contact.whatsapp?.replace(/\D/g, '')}`, '_blank')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(hotel.contact.phone, `priority-${hotel.id}`)}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      {copiedText === `priority-${hotel.id}` ? '✓ Copié' : 'Tel'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Templates de Communication */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <MessageSquare className="w-7 h-7 text-stratos-gold" />
              Templates de Communication Automatisée
            </CardTitle>
            <CardDescription className="text-base">
              Messages prêts à l'emploi - Personnalisez {'{'}nom_concierge{'}'}, {'{'}distance{'}'}, etc.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="whatsapp" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="whatsapp">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  WhatsApp
                </TabsTrigger>
                <TabsTrigger value="email">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="phone">
                  <Phone className="w-4 h-4 mr-2" />
                  Téléphone
                </TabsTrigger>
              </TabsList>

              <TabsContent value="whatsapp" className="mt-4">
                <div className="border-2 border-green-200 rounded-lg p-5 bg-green-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      Template WhatsApp Business
                    </h4>
                    <Button
                      onClick={() => copyToClipboard(strategy.communicationTemplates.whatsapp, 'whatsapp-tpl')}
                      variant="outline"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {copiedText === 'whatsapp-tpl' ? '✓ Copié !' : 'Copier'}
                    </Button>
                  </div>
                  <pre className="text-sm bg-white p-4 rounded border border-green-300 overflow-x-auto whitespace-pre-wrap font-sans">
                    {strategy.communicationTemplates.whatsapp}
                  </pre>
                </div>
              </TabsContent>

              <TabsContent value="email" className="mt-4">
                <div className="border-2 border-blue-200 rounded-lg p-5 bg-blue-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      Template Email Professionnel
                    </h4>
                    <Button
                      onClick={() => copyToClipboard(strategy.communicationTemplates.email, 'email-tpl')}
                      variant="outline"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {copiedText === 'email-tpl' ? '✓ Copié !' : 'Copier'}
                    </Button>
                  </div>
                  <pre className="text-xs bg-white p-4 rounded border border-blue-300 overflow-x-auto whitespace-pre-wrap max-h-96 font-sans">
                    {strategy.communicationTemplates.email}
                  </pre>
                </div>
              </TabsContent>

              <TabsContent value="phone" className="mt-4">
                <div className="border-2 border-purple-200 rounded-lg p-5 bg-purple-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg flex items-center gap-2">
                      <Phone className="w-5 h-5 text-purple-600" />
                      Script d'Appel Téléphonique
                    </h4>
                    <Button
                      onClick={() => copyToClipboard(strategy.communicationTemplates.phone, 'phone-tpl')}
                      variant="outline"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {copiedText === 'phone-tpl' ? '✓ Copié !' : 'Copier'}
                    </Button>
                  </div>
                  <pre className="text-sm bg-white p-4 rounded border border-purple-300 overflow-x-auto whitespace-pre-wrap font-sans">
                    {strategy.communicationTemplates.phone}
                  </pre>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>

      {/* Opportunités Privatisation & Groupes */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48 }}>
        <Card className="border-2 border-purple-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Users className="w-7 h-7 text-purple-600" />
              Opportunités Privatisation & Groupes
            </CardTitle>
            <CardDescription className="text-base">
              Pistes d'amélioration pour maximiser les revenus par événements et groupes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* B2C - Groupes Particuliers */}
              <div className="space-y-4">
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-5">
                  <h4 className="font-bold text-blue-900 text-lg mb-4 flex items-center gap-2">
                    <Users className="w-6 h-6" />
                    Groupes B2C - Particuliers
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-white p-4 rounded-lg border border-blue-200">
                      <h5 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        🎉 Événements Familiaux
                      </h5>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Anniversaires groupes (10-30 pers)</strong> : Menu spécial 45-65€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Réunions de famille</strong> : Brunch dominical 35-50€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Baby showers / Baptêmes</strong> : Formule déjeuner 40-55€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 font-bold">💡</span>
                          <span className="text-green-700 font-semibold">Potentiel: 4-6 événements/mois = +8k€/mois</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-blue-200">
                      <h5 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        🧳 Groupes Touristiques
                      </h5>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Tours gastronomiques</strong> : Partenariat avec tour operators</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Groupes amis (8-15 pers)</strong> : Menu dégustation 55-75€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Incentives hôteliers</strong> : Packages "Expérience lyonnaise"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 font-bold">💡</span>
                          <span className="text-green-700 font-semibold">Potentiel: 3-5 groupes/mois = +6k€/mois</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-blue-200">
                      <h5 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        💍 Mariages & Fiançailles
                      </h5>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Dîners de répétition</strong> : 20-40 invités, 60-90€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Brunchs post-mariage</strong> : 30-60 invités, 45-65€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">•</span>
                          <span><strong>Demandes en mariage privées</strong> : Privatisation 2-10 pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 font-bold">💡</span>
                          <span className="text-green-700 font-semibold">Potentiel: 2-4 événements/mois = +7k€/mois</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* B2B - Groupes Business */}
              <div className="space-y-4">
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-5">
                  <h4 className="font-bold text-orange-900 text-lg mb-4 flex items-center gap-2">
                    <Building className="w-6 h-6" />
                    Groupes Business - B2B
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-white p-4 rounded-lg border border-orange-200">
                      <h5 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                        💼 Déjeuners d'Affaires
                      </h5>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Déjeuners clients VIP</strong> : 4-12 pers, 55-85€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Repas équipe (mensuel)</strong> : 8-20 pers, contrats récurrents</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Petit-déj networking</strong> : 10-25 pers, 25-35€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 font-bold">💡</span>
                          <span className="text-green-700 font-semibold">Potentiel: 15-25 déj/mois = +12k€/mois</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-orange-200">
                      <h5 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                        🎯 Séminaires & Formation
                      </h5>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Déjeuners séminaires</strong> : 20-50 pers, pause-déj 30-45€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Coffee breaks premium</strong> : Formules 15-25€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Dîners clôture séminaire</strong> : 25-80 pers, 65-95€/pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 font-bold">💡</span>
                          <span className="text-green-700 font-semibold">Potentiel: 4-8 séminaires/mois = +15k€/mois</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-orange-200">
                      <h5 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                        🚀 Team Building & Événementiel
                      </h5>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Soirées team building</strong> : 15-40 pers, 50-75€/pers + boissons</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Apéros networking</strong> : Privatisation afterwork 20-50 pers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold">•</span>
                          <span><strong>Galettes des rois / Fêtes fin année</strong> : Saison haute Nov-Jan</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 font-bold">💡</span>
                          <span className="text-green-700 font-semibold">Potentiel: 5-10 événements/mois = +10k€/mois</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Privatisation */}
            <div className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-500 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="p-4 bg-green-500 rounded-full flex-shrink-0">
                  <Euro className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 text-2xl mb-3">
                    💎 Potentiel Total Privatisation & Groupes
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-lg border-2 border-blue-300">
                      <p className="text-sm text-gray-600 mb-1">B2C - Particuliers</p>
                      <p className="text-3xl font-bold text-blue-600">21k€/mois</p>
                      <p className="text-xs text-gray-500 mt-1">252k€/an (9-15 événements/mois)</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-orange-300">
                      <p className="text-sm text-gray-600 mb-1">B2B - Business</p>
                      <p className="text-3xl font-bold text-orange-600">37k€/mois</p>
                      <p className="text-xs text-gray-500 mt-1">444k€/an (24-43 événements/mois)</p>
                    </div>
                  </div>
                  <div className="bg-green-600 text-white p-5 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-90 mb-1">Total Additionnel Annuel</p>
                        <p className="text-5xl font-bold">696k€</p>
                        <p className="text-sm mt-2 opacity-90">+ Partenariats hôtels (118k€) = <strong className="text-yellow-300">814k€/an</strong></p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-yellow-400 text-gray-900 text-lg px-4 py-2 mb-2">
                          +58k€/mois
                        </Badge>
                        <p className="text-xs opacity-90">Stratégie combinée</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Recommandées */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-5">
                <h4 className="font-bold text-purple-900 text-lg mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Actions Immédiates - B2C
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Créer page web "Privatisation"</strong> : Formulaire + tarifs packages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Partenariat wedding planners</strong> : Commission 10-15% sur apports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Instagram Stories</strong> : Montrer ambiance privatisations passées</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Offre early bird</strong> : -15% si résa 3 mois avant événement</span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-5">
                <h4 className="font-bold text-orange-900 text-lg mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Actions Immédiates - B2B
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>LinkedIn B2B prospecting</strong> : Cibler DRH, Office Managers Lyon</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Packages séminaires clés en main</strong> : Petit-déj + déj + dîner</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Partenariat espaces coworking</strong> : WeWork, Spaces, Morning (Lyon)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Contrats récurrents entreprises</strong> : Forfait mensuel déjeuners équipe</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Workflow Automatisation */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Zap className="w-7 h-7 text-yellow-600" />
              Workflow d'Automatisation - 7 Étapes
            </CardTitle>
            <CardDescription className="text-base">
              Séquence optimisée pour maximiser le taux de conversion (objectif 67%)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {strategy.automationWorkflow.map((step) => (
                <div
                  key={step.step}
                  className="flex items-start gap-4 p-5 bg-gray-50 rounded-lg border hover:border-stratos-gold transition-colors"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-stratos-gold text-white font-bold text-xl flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-gray-900 text-lg">{step.action}</h4>
                      <Badge variant="outline" className="text-sm">
                        {step.channel === 'whatsapp' && <MessageSquare className="w-3 h-3 mr-1" />}
                        {step.channel === 'email' && <Mail className="w-3 h-3 mr-1" />}
                        {step.channel === 'phone' && <Phone className="w-3 h-3 mr-1" />}
                        {step.channel}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">{step.timing}</span>
                    </div>
                    <p className="text-sm text-gray-700 bg-white p-4 rounded border leading-relaxed">
                      {step.template}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <h4 className="font-bold text-yellow-900 text-lg">Recommandations Critiques</h4>
                  <ul className="text-sm text-yellow-800 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>Personnaliser CHAQUE message (nom concierge, distance exacte, hôtel)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>Respecter le timing : ne pas spammer (J1 WhatsApp, J3 Email, J5 Phone)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>Tracker réponses dans CRM (Google Sheet simple suffit)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span><strong>80% signent après le 3ème contact</strong> - Ne pas abandonner trop tôt !</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
